import { useEffect, useState } from "react";
import {
  Plus,
  PencilSimple,
  Trash,
  CircleNotch,
  X,
  Eye,
  EyeSlash,
  FilePdf,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import {
  listBrochures,
  createBrochure,
  updateBrochure,
  deleteBrochure,
} from "@/lib/api";
import FileUploadField from "./FileUploadField";

const EMPTY = {
  title: "",
  description: "",
  url: "",
  sort_order: 0,
  is_published: true,
};

export default function BrochuresPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    setError("");
    listBrochures({ all: true })
      .then((r) => setItems(r.items || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (payload) => {
    setBusy(true);
    setError("");
    try {
      if (payload.id) {
        await updateBrochure(payload.id, payload);
      } else {
        await createBrochure(payload);
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteBrochure(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Brochure ufficiali</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tighter font-medium">
            {items.length}{" "}
            <span className="text-[#5C5A55] font-light">
              {items.length === 1 ? "brochure" : "brochure"}
            </span>
          </h2>
          <p className="text-sm text-[#5C5A55] mt-2 max-w-2xl">
            Mostrate nella sezione «Brochure ufficiali Korus» in fondo alla
            homepage. L'ordine segue il valore <em>Ordine di visualizzazione</em>.
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="brochures-add-btn"
          className="btn-primary"
        >
          <Plus size={16} weight="bold" />
          Nuova brochure
        </button>
      </div>

      {error && (
        <div className="p-3 bg-[#EB0D0D]/8 text-[#C00A0A] text-sm border border-[#EB0D0D]/30">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <CircleNotch
            size={28}
            weight="bold"
            className="animate-spin text-[#EB0D0D]"
          />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[#F0EFEB] border border-[#D1CFC7]/40 py-16 text-center text-[#5C5A55]">
          Nessuna brochure ancora. Clicca "Nuova brochure" per iniziare.
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-brochures-list">
          {items.map((b) => (
            <div
              key={b.id}
              className={`bg-[#F0EFEB] p-5 border border-[#D1CFC7]/40 grid grid-cols-1 md:grid-cols-12 gap-4 items-center ${
                !b.is_published ? "opacity-60" : ""
              }`}
              data-testid={`brochure-row-${b.id}`}
            >
              <div className="md:col-span-1">
                <div className="w-16 h-16 bg-[#FAF9F6] border border-[#D1CFC7] flex items-center justify-center">
                  <FilePdf size={28} weight="duotone" className="text-[#EB0D0D]" />
                </div>
              </div>
              <div className="md:col-span-6 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#5C5A55]">
                    Ordine {b.sort_order}
                  </span>
                  {!b.is_published && (
                    <span className="text-[10px] uppercase tracking-[0.15em] font-bold bg-[#5C5A55] text-[#FAF9F6] px-2 py-0.5">
                      Nascosta
                    </span>
                  )}
                </div>
                <div className="font-display text-lg truncate">{b.title}</div>
                {b.description && (
                  <div className="text-xs text-[#5C5A55] truncate">
                    {b.description}
                  </div>
                )}
              </div>
              <div className="md:col-span-3 min-w-0">
                <a
                  href={b.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[#EB0D0D] hover:underline inline-flex items-center gap-1 truncate max-w-full"
                  title={b.url}
                >
                  <ArrowSquareOut size={12} weight="bold" className="flex-shrink-0" />
                  <span className="truncate">Apri PDF</span>
                </a>
              </div>
              <div className="md:col-span-2 flex md:justify-end gap-1">
                <button
                  onClick={() => setEditing({ ...b })}
                  className="p-2 hover:bg-[#FAF9F6] text-[#1A1918] transition-colors"
                  title="Modifica"
                  data-testid={`edit-brochure-${b.id}`}
                >
                  <PencilSimple size={16} weight="regular" />
                </button>
                <button
                  onClick={() => setDeleteTarget(b)}
                  className="p-2 hover:bg-[#EB0D0D]/10 text-[#EB0D0D] transition-colors"
                  title="Elimina"
                  data-testid={`delete-brochure-${b.id}`}
                >
                  <Trash size={16} weight="regular" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <BrochureEditModal
          initial={editing}
          onSave={save}
          onCancel={() => setEditing(null)}
          busy={busy}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          title={deleteTarget.title}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          busy={busy}
        />
      )}
    </div>
  );
}

function BrochureEditModal({ initial, onSave, onCancel, busy }) {
  const [form, setForm] = useState(initial);
  const isNew = !initial.id;
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const canSave = form.title.trim() && form.url.trim();

  const onSubmit = (e) => {
    e.preventDefault();
    if (!canSave) return;
    onSave(form);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1A1918]/60 backdrop-blur-sm"
      onClick={busy ? null : onCancel}
    >
      <form
        onSubmit={onSubmit}
        className="bg-[#FAF9F6] w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#D1CFC7] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="brochure-edit-modal"
      >
        <div className="flex items-start justify-between p-6 md:p-8 border-b border-[#D1CFC7]/60 sticky top-0 bg-[#FAF9F6] z-10">
          <div>
            <div className="overline mb-1">
              {isNew ? "Nuova brochure" : "Modifica brochure"}
            </div>
            <h3 className="font-display text-2xl tracking-tighter font-medium">
              {form.title || "Senza titolo"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="p-2 hover:bg-[#F0EFEB] transition-colors disabled:opacity-50"
            aria-label="Chiudi"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-5">
          <Field label="Titolo *">
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder="Es. Catalogo Win Box"
              className="w-full bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3 focus:outline-none focus:border-[#EB0D0D]"
              data-testid="brochure-title-input"
            />
          </Field>

          <Field label="Descrizione">
            <input
              type="text"
              value={form.description || ""}
              onChange={(e) => update({ description: e.target.value })}
              placeholder="Es. Linea PVC · Win · Win Slide · Lumen"
              className="w-full bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3 focus:outline-none focus:border-[#EB0D0D]"
              data-testid="brochure-description-input"
            />
          </Field>

          <FileUploadField
            kind="pdf"
            label="File PDF *"
            value={form.url || null}
            onChange={(url) => update({ url: url || "" })}
            hint="PDF · max 20MB"
          />

          <Field label="Ordine di visualizzazione">
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) =>
                update({ sort_order: parseInt(e.target.value, 10) || 0 })
              }
              className="w-full max-w-[180px] bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3 focus:outline-none focus:border-[#EB0D0D]"
            />
          </Field>

          <div className="border-t border-[#D1CFC7]/60 pt-5">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.is_published}
                onChange={(e) => update({ is_published: e.target.checked })}
                className="w-4 h-4 accent-[#EB0D0D]"
              />
              <span className="inline-flex items-center gap-2 text-sm text-[#1A1918]">
                {form.is_published ? (
                  <Eye size={16} weight="bold" className="text-[#EB0D0D]" />
                ) : (
                  <EyeSlash size={16} weight="bold" className="text-[#5C5A55]" />
                )}
                Pubblicata sul sito
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-6 md:p-8 border-t border-[#D1CFC7]/60 bg-[#F0EFEB] sticky bottom-0">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-transparent text-[#1A1918] border border-[#D1CFC7] hover:border-[#1A1918] transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={busy || !canSave}
            data-testid="brochure-save-btn"
            className="btn-primary disabled:opacity-50"
          >
            {busy ? (
              <>
                <CircleNotch size={14} weight="bold" className="animate-spin" />
                Salvataggio…
              </>
            ) : (
              <>{isNew ? "Crea brochure" : "Salva modifiche"}</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function DeleteConfirmModal({ title, onConfirm, onCancel, busy }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#1A1918]/60 backdrop-blur-sm"
      onClick={busy ? null : onCancel}
    >
      <div
        className="bg-[#FAF9F6] w-full max-w-md border border-[#D1CFC7] shadow-2xl p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="overline mb-3">Conferma eliminazione</div>
        <h3 className="font-display text-2xl tracking-tighter font-medium mb-3">
          Eliminare{" "}
          <em className="italic font-light text-[#EB0D0D]">{title}</em>?
        </h3>
        <p className="text-sm text-[#5C5A55] mb-6">
          La brochure verrà rimossa dalla homepage. Il PDF caricato resta nella
          libreria media.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-transparent text-[#1A1918] border border-[#D1CFC7] hover:border-[#1A1918] transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="brochure-delete-confirm"
            className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-[#EB0D0D] text-[#FAF9F6] hover:bg-[#C00A0A] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy ? (
              <CircleNotch size={14} weight="bold" className="animate-spin" />
            ) : (
              <Trash size={14} weight="bold" />
            )}
            Elimina
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] font-bold mb-2 text-[#1A1918]">
        {label}
      </label>
      {children}
    </div>
  );
}
